/* ============================================================================
 * cz-sticky-header.js — StickyHeader custom element
 *
 * Estratto dal blocco {% javascript %} di sections/header.liquid perché su
 * theme dedicati `--unpublished` appena creati Shopify non sempre compila
 * automaticamente i blocchi {% javascript %} in asset esterni (header.js 404).
 * Caricando il codice come asset standalone bypassiamo quel problema.
 * Behavior identico, nessun cambio funzionale.
 * ============================================================================ */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};
    this.isHome = document.body.classList.contains('template-index');

    this.setHeaderHeight();

    if (window.ResizeObserver) {
      this.headerResizeObserver = new ResizeObserver(() => this.setHeaderHeight());
      this.headerResizeObserver.observe(this.header);
    }

    if (this.isHome) this.setupHomeHero();

    window.matchMedia('(max-width: 990px)').addEventListener('change', () => {
      this.setHeaderHeight();
      if (this.isHome) this.computeHeroBounds();
    });

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.isReversing = false;
    this.hideStickyTimer = null;
    this.reverseTimer1 = null;
    this.reverseTimer2 = null;
    this.predictiveSearch = document.querySelector('cz-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    const h = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${h}px`);
    if (!this.header.classList.contains('shopify-section-header-sticky')) {
      document.documentElement.style.setProperty('--cz-flow-header-height', `${h}px`);
    }
  }

  setupHomeHero() {
    this.hero =
      document.querySelector('.banner--screen') ||
      document.querySelector('main .banner') ||
      document.querySelector('main > .shopify-section');
    if (!this.hero) return;
    this.computeHeroBounds();
    if (window.ResizeObserver) {
      this.heroResizeObserver = new ResizeObserver(this.computeHeroBounds.bind(this));
      this.heroResizeObserver.observe(this.hero);
    } else {
      window.addEventListener('resize', this.computeHeroBounds.bind(this));
    }
  }

  computeHeroBounds() {
    if (!this.hero) return;
    this.heroHideThreshold = this.hero.offsetHeight / 5;
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });
    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    const CZ_NON_HOME_HIDE = 100;
    const CZ_NON_HOME_RESET_TOP = 400;
    const isDesktopNonHome = !this.isHome && window.innerWidth >= 990;
    let thresholdBottom;
    let thresholdTop;
    if (this.isHome && this.heroHideThreshold !== undefined) {
      thresholdBottom = this.heroHideThreshold;
      thresholdTop = this.heroHideThreshold;
    } else if (isDesktopNonHome) {
      thresholdBottom = CZ_NON_HOME_HIDE;
      thresholdTop = CZ_NON_HOME_RESET_TOP;
    } else {
      thresholdBottom = this.headerBounds.bottom;
      thresholdTop = this.headerBounds.top;
    }

    if (scrollTop > this.currentScrollTop && scrollTop > thresholdBottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop <= thresholdTop) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > thresholdBottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= thresholdTop) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;

    if (this.reverseTimer1) { clearTimeout(this.reverseTimer1); this.reverseTimer1 = null; }
    if (this.reverseTimer2) { clearTimeout(this.reverseTimer2); this.reverseTimer2 = null; }
    this.isReversing = false;
    this.header.style.transition = '';
    this.header.style.transform = '';

    this.header.classList.add('shopify-section-header-hidden');
    if (!this.hideStickyTimer) {
      this.hideStickyTimer = setTimeout(() => {
        this.header.classList.add('shopify-section-header-sticky');
        this.hideStickyTimer = null;
      }, 550);
    }

    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;

    if (this.reverseTimer1) { clearTimeout(this.reverseTimer1); this.reverseTimer1 = null; }
    if (this.reverseTimer2) { clearTimeout(this.reverseTimer2); this.reverseTimer2 = null; }
    if (this.hideStickyTimer) { clearTimeout(this.hideStickyTimer); this.hideStickyTimer = null; }
    this.isReversing = false;
    this.header.style.transition = '';
    this.header.style.transform = '';

    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    if (this.isReversing) return;

    if (this.hideStickyTimer) { clearTimeout(this.hideStickyTimer); this.hideStickyTimer = null; }

    const fromCompact = this.header.classList.contains('shopify-section-header-sticky');
    const shouldChoreograph = fromCompact && (this.isHome || window.innerWidth >= 990);

    if (shouldChoreograph) {
      this.runReverseSticky();
      return;
    }

    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  runReverseSticky() {
    this.isReversing = true;

    if (this.isHome) {
      this.header.style.transition = 'top 0.35s cubic-bezier(0.22, 0.61, 0.36, 1)';
      this.header.classList.add('shopify-section-header-hidden');

      this.reverseTimer1 = setTimeout(() => {
        var wrapper = this.header.querySelector('.header-wrapper');
        this.header.style.transition = 'top 0.55s cubic-bezier(0.22, 0.61, 0.36, 1)';
        if (wrapper) wrapper.style.transition = 'none';

        this.header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate'
        );
        // eslint-disable-next-line no-unused-expressions
        this.header.offsetHeight;
        if (wrapper) {
          // eslint-disable-next-line no-unused-expressions
          wrapper.offsetHeight;
          wrapper.style.transition = '';
        }
        this.reverseTimer1 = null;

        this.reverseTimer2 = setTimeout(() => {
          this.header.style.transition = '';
          this.isReversing = false;
          this.reverseTimer2 = null;
        }, 580);
      }, 370);
    } else {
      const alreadyHidden = this.header.classList.contains('shopify-section-header-hidden');

      if (!alreadyHidden) {
        this.header.classList.add('shopify-section-header-hidden');
      }

      this.reverseTimer1 = setTimeout(() => {
        this.header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate'
        );

        this.reverseTimer1 = null;

        this.reverseTimer2 = setTimeout(() => {
          this.isReversing = false;
          this.reverseTimer2 = null;
        }, 580);
      }, alreadyHidden ? 0 : 370);
    }
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    // L'header nuovo può non avere il details-modal della ricerca Dawn:
    // senza guardia questo esplodeva a ogni scroll (~95 errori a sessione).
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    this.searchModal?.close(false);
  }
}

customElements.define('sticky-header', StickyHeader);
